import { HTTP_API_V2 } from '../../http-common'
import { appendNextPage, mergeRefreshedFirstPage } from '../../utils/notification-history'

// Page size of the notification history (the API accepts up to 100).
const PAGE_SIZE = 20

export default {
  state: {
    notifications: [],
    notificationsTotal: 0,
    notificationsUnviewed: 0,
    notificationsLoaded: false,
    notificationsLoading: false,
    notificationsError: false
  },
  getters: {
    notificationHistory (state) {
      return state.notifications
    },
    notificationHistoryTotal (state) {
      return state.notificationsTotal
    },
    notificationHistoryUnviewed (state) {
      return state.notificationsUnviewed
    },
    notificationHistoryLoaded (state) {
      return state.notificationsLoaded
    },
    notificationHistoryLoading (state) {
      return state.notificationsLoading
    },
    notificationHistoryError (state) {
      return state.notificationsError
    },
    notificationHistoryHasMore (state) {
      return state.notifications.length < state.notificationsTotal
    }
  },
  mutations: {
    // A refresh (first page) merges into what is loaded instead of replacing
    // it, so older pages loaded with "Load more" are not dropped.
    setNotificationHistory (state, { notifications, total, unviewed, append }) {
      state.notifications = append
        ? appendNextPage(state.notifications, notifications)
        : mergeRefreshedFirstPage(state.notifications, notifications)
      state.notificationsTotal = total
      state.notificationsUnviewed = unviewed
      state.notificationsLoaded = true
    },
    setNotificationHistoryLoading (state, loading) {
      state.notificationsLoading = loading
    },
    setNotificationHistoryError (state, error) {
      state.notificationsError = error
    },
    markNotificationAsViewed (state, { id, viewedAt }) {
      const notification = state.notifications.find(item => item.id === id)
      if (notification && !notification.viewed) {
        notification.viewed = true
        notification.viewed_at = viewedAt
        state.notificationsUnviewed = Math.max(0, state.notificationsUnviewed - 1)
      }
    },
    markAllNotificationsAsViewed (state) {
      const viewedAt = new Date().toISOString()
      state.notifications.forEach(notification => {
        if (!notification.viewed) {
          notification.viewed = true
          notification.viewed_at = viewedAt
        }
      })
      state.notificationsUnviewed = 0
    },
    clearNotificationHistory (state) {
      state.notifications = []
      state.notificationsTotal = 0
      state.notificationsUnviewed = 0
      state.notificationsLoaded = false
      state.notificationsError = false
    }
  },
  actions: {
    // Loads the first page, or the next one with { loadMore: true }.
    async fetchNotificationHistory ({ commit, state }, { loadMore = false } = {}) {
      if (state.notificationsLoading) {
        return
      }
      commit('setNotificationHistoryLoading', true)
      try {
        const offset = loadMore ? state.notifications.length : 0
        const response = await HTTP_API_V2.get('/notifications/history', { params: { limit: PAGE_SIZE, offset } })
        commit('setNotificationHistory', {
          notifications: response.data.notifications,
          total: response.data.total,
          unviewed: response.data.unviewed,
          append: loadMore
        })
        commit('setNotificationHistoryError', false)
      } catch (error) {
        commit('setNotificationHistoryError', true)
        console.error('Failed to load the notification history', error)
      } finally {
        commit('setNotificationHistoryLoading', false)
      }
    },
    async markNotificationViewed ({ commit, dispatch, state }, notificationId) {
      try {
        const response = await HTTP_API_V2.patch(`/notifications/history/${notificationId}/viewed`)
        const isLoaded = state.notifications.some(notification => notification.id === notificationId)
        if (isLoaded) {
          commit('markNotificationAsViewed', { id: notificationId, viewedAt: response.data.viewed_at })
        } else {
          // Opened from a push not loaded yet (e.g. older than the first page)
          await dispatch('fetchNotificationHistory')
        }
      } catch (error) {
        // A push can be tapped before its record is saved, or after it expired.
        console.error('Failed to mark the notification as viewed', error)
      }
    },
    async markAllNotificationsViewed ({ commit }) {
      try {
        await HTTP_API_V2.patch('/notifications/history/viewed')
        commit('markAllNotificationsAsViewed')
      } catch (error) {
        console.error('Failed to mark all notifications as viewed', error)
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/notification_history/index.js